import { takeLatest, call, put } from 'redux-saga/effects';
import { CapabilityApi, GatewayManagementApi, AssetDetailsApi } from 'api';
import { apiCallStatus, NOTIFICATION_TYPES } from 'helpers/constants';
import logger from 'helpers/logger';
import { actionTypes, GATEWAY_CAPABILITIES_LIST } from './constants';
import { getGatewayInfoAction, getGatewayConnectedAssetsAction, getUserCapabilitiesAction, getDownloadInstallationReportAction, getDownloadDiagnosticReportAction, getConnectionHistoryGatewayAction, getConnectionHistoryAssetAction, getConnectedHistoryAction } from './actions';
import { getRssiHistoryAction } from './ConnectedAssets/ConnectedAssetsDrives/actions';
import { actionTypes as connectedAssetActionTypes } from './ConnectedAssets/ConnectedAssetsDrives/constants';
import { translate } from 'helpers/translateHelper';
import { notify } from 'sharedComponents/GeneralNotification';
import { downloadFile, b64toBlob } from 'helpers/download-file-util';

function* getGatewayInfo(action) {
    const id = action.payload?.gatewayID;
    const isSyntheticGateway = id ? false : true;
    yield put(getGatewayInfoAction(apiCallStatus.LOADING));
    try {
        const response = yield call(GatewayManagementApi.getGatewayDetails, id, isSyntheticGateway);
        yield put(getGatewayInfoAction(apiCallStatus.SUCCESS, response));
        logger.debug('Gateway Details successfully loaded for page ');
    } catch (err) {
        yield put(getGatewayInfoAction(apiCallStatus.ERROR, [], err));
    }
}

function* getGatewayConnectedAssets(action) {
    const id = action.payload?.gatewayID;
    const isSyntheticGateway = id ? false : true;
    yield put(getGatewayConnectedAssetsAction(apiCallStatus.LOADING));
    try {
        const response = yield call(GatewayManagementApi.getGatewayConnectAssets, id, isSyntheticGateway);
        yield put(getGatewayConnectedAssetsAction(apiCallStatus.SUCCESS, response));
        logger.debug('Gateway Details successfully loaded for page ');
    } catch (err) {
        yield put(getGatewayConnectedAssetsAction(apiCallStatus.ERROR, [], err));
    }
}

function* getDCUUserCapabilities(action) {
    const ducId = action.payload?.dcuId;
    yield put(getUserCapabilitiesAction(apiCallStatus.LOADING));
    try {
        const response = yield call(CapabilityApi.checkGatewayCapabilities, GATEWAY_CAPABILITIES_LIST, ducId);
        yield put(getUserCapabilitiesAction(apiCallStatus.SUCCESS, response));
    } catch (error) {
        yield put(getUserCapabilitiesAction(apiCallStatus.ERROR, [], error));
        notify(translate('ABB.Powertrain.Frontend.userCapabilityError'), NOTIFICATION_TYPES.ERROR, true);
        logger.error(error?.message);
    }
}

function* getDownloadInstallationReport(action) {
    const ducId = action?.gatewayId;
    const fileType = action?.fileType;
    yield put(getDownloadInstallationReportAction(apiCallStatus.LOADING));
    try {
        const response = yield call(GatewayManagementApi.downloadInstallationReport, ducId, fileType);
        if (response?.errorMessage) {
            notify(response?.errorMessage, NOTIFICATION_TYPES.ERROR, true);
            logger.error(response?.errorMessage);
            return;
        }
        const fileBlob = b64toBlob(response?.fileContent);
        downloadFile(response?.fileName, fileBlob);
        yield put(getDownloadInstallationReportAction(apiCallStatus.SUCCESS, response));
        notify(translate('ABB.Powertrain.Frontend.gatewayInstallationReportDownloadedSuccessfully'), NOTIFICATION_TYPES.SUCCESS);
        logger.debug('Installation report downloaded successfully');
    } catch (error) {
        yield put(getDownloadInstallationReportAction(apiCallStatus.ERROR, [], error));
        notify(translate('ABB.Powertrain.Frontend.gatewayInstallationReportDownloadFailed'), NOTIFICATION_TYPES.ERROR, true);
        logger.error(error?.message);
    }
}

function* getDownloadDiagnosticReport(action) {
    const dcuId = action?.dcuId;
    yield put(getDownloadDiagnosticReportAction(apiCallStatus.LOADING));
    try {
        const response = yield call(GatewayManagementApi.downloadDiagnosticReport, dcuId);
        const fileBlob = b64toBlob(response.fileBytes);
        downloadFile(response.fileName, fileBlob);
        yield put(getDownloadDiagnosticReportAction(apiCallStatus.SUCCESS, response));
        notify(translate('ABB.Powertrain.Frontend.gatewayDiagnosticReportDownloadedSuccessfully'), NOTIFICATION_TYPES.SUCCESS);
        logger.debug('Diagnostic report downloaded successfully');
    } catch (error) {
        yield put(getDownloadDiagnosticReportAction(apiCallStatus.ERROR, [], error));
        notify(translate('ABB.Powertrain.Frontend.gatewayDiagnosticReportDownloadFailed'), NOTIFICATION_TYPES.ERROR, true);
        logger.error(error?.message);
    }
}

function* getGatewayConnectionHistory(action) {
    const gatewayId = action?.payload?.gatewayID;
    yield put(getConnectionHistoryGatewayAction(apiCallStatus.LOADING));
    try {
        const response = yield call(GatewayManagementApi.getGatewayConnectionHistory, gatewayId);
        yield put(getConnectionHistoryGatewayAction(apiCallStatus.SUCCESS, response));
    } catch (error) {
        yield put(getConnectionHistoryGatewayAction(apiCallStatus.ERROR, [], error));
        logger.error(error?.message);
    }
}

function* getAssetConnectionHistory(action) {
    const assetId = action?.payload?.assetId;
    yield put(getConnectionHistoryAssetAction(apiCallStatus.LOADING));
    try {
        const response = yield call(GatewayManagementApi.getAssetConnectionHistory, assetId);
        yield put(getConnectionHistoryAssetAction(apiCallStatus.SUCCESS, response));
    } catch (error) {
        yield put(getConnectionHistoryAssetAction(apiCallStatus.ERROR, [], error));
        logger.error(error?.message);
    }
}

// eslint-disable-next-line import/no-unused-modules
export function* GatewayManagementInfoWatchers() {
    yield takeLatest(actionTypes.GET_GATEWAY_INFO, getGatewayInfo);
}

// eslint-disable-next-line import/no-unused-modules
export function* GatewayManagementConnectedAssetsWatchers() {
    yield takeLatest(actionTypes.GET_GATEWAY_CONNECTED_ASSETS, getGatewayConnectedAssets);
}

// eslint-disable-next-line import/no-unused-modules
export function* GatewayUserCapabilitiesWatchers() {
    yield takeLatest(actionTypes.GET_USER_CAPABILITY, getDCUUserCapabilities);
}

// eslint-disable-next-line import/no-unused-modules
export function* GatewayManagementDownloadInstallationReportWatchers() {
    yield takeLatest(actionTypes.DOWNLOAD_INSTALLATION_REPORT, getDownloadInstallationReport);
}

// eslint-disable-next-line import/no-unused-modules
export function* GatewayManagementDownloadDiagnosticReportWatchers() {
    yield takeLatest(actionTypes.DOWNLOAD_DIAGNOSTIC_REPORT, getDownloadDiagnosticReport);
}

export function* GatewayManagementConnectionHistoryGatewayWatchers() {
    yield takeLatest(actionTypes.GET_CONNECTION_HISTORY_GATEWAY, getGatewayConnectionHistory);
}

export function* GatewayManagementConnectionHistoryAssetWatchers() {
    yield takeLatest(actionTypes.GET_CONNECTION_HISTORY_ASSET, getAssetConnectionHistory);
}

function* getConnectedHistory(action) {
    const isSyntheticGateway = !action.payload?.gatewayId;
    const { gatewayId, pageSize, keyword, sortBy, startTime, endTime, nextCursor, prevCursor } = action.payload || {};
    yield put(getConnectedHistoryAction(apiCallStatus.LOADING));
    try {
        const isPaginationRequest = !!(nextCursor || prevCursor);
        const requestPayload = {
            gatewayId,
            isSyntheticGateway,
            pageSize,
            keyword,
            sortBy,
            startTime: isPaginationRequest ? null : startTime,
            endTime: isPaginationRequest ? null : endTime,
            nextCursor,
            prevCursor,
            assetIds: [],
        };
        const response = yield call(GatewayManagementApi.getAssetConnectionStatusHistory, requestPayload);
        yield put(getConnectedHistoryAction(apiCallStatus.SUCCESS, response));
    } catch (error) {
        yield put(getConnectedHistoryAction(apiCallStatus.ERROR, [], error));
        logger.error(error?.message);
    }
}

function* getRssiHistory(action) {
    const sensorId = action?.payload?.sensorId;
    yield put(getRssiHistoryAction(apiCallStatus.LOADING));
    try {
        const response = yield call(AssetDetailsApi.getRssiValue, sensorId);
        yield put(getRssiHistoryAction(apiCallStatus.SUCCESS, response));
    } catch (error) {
        yield put(getRssiHistoryAction(apiCallStatus.ERROR, [], error));
        logger.error(error?.message);
    }
}

export function* GatewayManagementConnectedHistoryWatchers() {
    yield takeLatest(actionTypes.GET_CONNECTED_HISTORY, getConnectedHistory);
}

export function* GatewayManagementRssiHistoryWatchers() {
    yield takeLatest(connectedAssetActionTypes.GET_RSSI_HISTORY, getRssiHistory);
}
